import { useEffect, useMemo } from 'react';
import type { IMessage } from 'react-native-gifted-chat';

import type { ICustomMessage } from '@/components/chat';
import { selectOutbox, useOutboxStore } from '@/services/chat/outboxStore';

/** Keys an outbox message apart from every stored one, whose `_id` is its index. */
const OUTBOX_KEY_PREFIX = 'outbox:';

/**
 * The reader's sends the conversation does not hold yet, as chat messages,
 * newest first like the stored ones, to sit in front of them.
 *
 * An entry is dropped the moment a stored message carries its
 * `clientMessageId`, so the pending bubble and its stored copy never show
 * together. Until then it renders as the reader's own message with its send
 * status beneath. Nothing is in the outbox unless the conversation's sends
 * are idempotent, so for every transport today this returns nothing.
 */
export function useOutboxMessages(
  conversationId: string,
  storedMessages: readonly IMessage[],
  viewerId: string
): ICustomMessage[] {
  const entries = useOutboxStore(selectOutbox(conversationId));

  const deliveredIds = useMemo(() => {
    const ids = new Set<string>();

    for (const message of storedMessages) {
      const { clientMessageId } = message as ICustomMessage;

      if (clientMessageId) {
        ids.add(clientMessageId);
      }
    }

    return ids;
  }, [storedMessages]);

  useEffect(() => {
    if (entries.some((entry) => deliveredIds.has(entry.message.clientMessageId))) {
      useOutboxStore.getState().settle(conversationId, deliveredIds);
    }
  }, [conversationId, deliveredIds, entries]);

  return useMemo(
    () =>
      entries
        .filter((entry) => !deliveredIds.has(entry.message.clientMessageId))
        .map(
          (entry): ICustomMessage => ({
            _id: `${OUTBOX_KEY_PREFIX}${entry.message.clientMessageId}`,
            messageId: `${OUTBOX_KEY_PREFIX}${entry.message.clientMessageId}`,
            clientMessageId: entry.message.clientMessageId,
            text: entry.message.body,
            createdAt: entry.createdAt,
            user: { _id: viewerId },
            pending: entry.status === 'pending',
            outboxStatus: entry.status,
          })
        )
        .reverse(),
    [deliveredIds, entries, viewerId]
  );
}
